import {
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  Modal,
  TextField,
  Typography
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import React, { useEffect, useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import SmartLockApiCallerInstance from '../utils/SmartLockApiCallerInstance';
import { ReactComponent as PlusIcon } from '../icons/PLUSicon.svg';
import { SMART_LOCK_API_ENDPOINTS } from '../URLConstants';
import SwitchButton from './_dashboard/smartlock/SwitchButton';
import { GetRfidCardList, UpdateRfidCardDetail } from '../network/Devices';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '250px', md: '350px', lg: '493px' },
  bgcolor: 'background.paper',
  p: { xs: 2, lg: 4 },
  borderRadius: 1,
  height: '480px',
  overflowX: 'hidden',
  display: 'flex',
  flexDirection: 'column'
};

const textStyle = {
  fontFamily: 'mulish',
  fontSize: '12px',
  fontWeight: '600',
  lineHeight: '15.06px',
  textAlign: 'left'
};

const textError = {
  fontSize: '12px',
  fontWeight: '600',
  lineHeight: '14px',
  color: 'red',
  marginTop: '5px'
};
const importantText = {
  fontSize: '14px',
  fontWeight: '600',
  lineHeight: '18px',
  color: '#C30A0A',
  marginBottom: '10px'
};

export default function RfidManagementModal({
  openAssignedModal,
  handleClose,
  deviceId,
  alertMetas,
  setAlertMetas
}) {
  const [rfidName, setRfidName] = useState('');
  const [rfidCardList, setRfidCardList] = useState([]);
  const [error, setError] = useState(false);
  const [loader, setLoader] = useState(false);

  const addRfidDetails = (e) => {
    e.preventDefault();
    if (rfidName?.length === 0) {
      setError(true);
    } else {
      setError(false);
      if (rfidCardList?.length < 4) {
        setRfidCardList([
          ...rfidCardList.map((item) => ({
            rfidCardNo: item.rfidCardNo,
            status: item.status
          })),
          { rfidCardNo: rfidName, status: true }
        ]);
      }
    }
    if (rfidCardList.length === 4) {
      setError(false);
    }
    setRfidName('');
  };

  const addRfidCardData = async (e) => {
    e.preventDefault();
    setLoader(true);
    if (!error) {
      try {
        const res = await UpdateRfidCardDetail(deviceId, rfidCardList);
        if (res) {
          setLoader(false);
          setAlertMetas({
            ...alertMetas,
            severity: 'success',
            snackBarOpened: true,
            msg: res?.data?.data
          });
          handleClose();
          setTimeout(() => setAlertMetas({ ...alertMetas, snackBarOpened: false, msg: '' }), 4000);
        }
      } catch (error) {
        setLoader(false);
        setAlertMetas({
          ...alertMetas,
          severity: 'error',
          snackBarOpened: true,
          msg: error?.response?.data?.message
        });
        handleClose();
        setTimeout(() => setAlertMetas({ ...alertMetas, snackBarOpened: false, msg: '' }), 4000);
      }
    }
  };

  const deleteRfidDetail = (id) => {
    setRfidCardList(rfidCardList.filter((item) => id !== item.id));
  };

  const handleRfidStatus = (id) => {
    setRfidCardList((prevList) =>
      prevList.map((item) => (item.id === id ? { ...item, status: !item.status } : item))
    );
  };

  const getRfidCards = async () => {
    try {
      const res = await GetRfidCardList(deviceId);
      if (res) {
        setRfidCardList(res?.data?.data);
      }
    } catch (error) {
      console.log(error, 'err');
    }
  };

  useEffect(() => {
    getRfidCards();
  }, []);

  return (
    <Box>
      <Modal
        open={openAssignedModal}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        data-testid="rfid-management-modal"
      >
        <Box sx={style} style={{ backgroundColor: '#F8FAFB' }}>
          <Typography sx={{ fontSize: '15px', fontWeight: 900, marginBottom: '20px' }}>
            RFID Management
          </Typography>
          <Typography sx={importantText}>A maximum of 4 cards can be added.</Typography>

          <Typography sx={textStyle}>Edit existing RFID cards or link new RFID cards.</Typography>

          <Box
            sx={{
              borderRadius: '10px',
              backgroundColor: '#FFFFFF',
              padding: { xs: '10px', lg: '10px 20px' },
              marginTop: '10px'
            }}
          >
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  border: '0px solid #B0B9C7',
                  borderRadius: '10px',
                  gap: '10px'
                }}
              >
                <FormControl data-testid="rfid-input-formcontrol" fullWidth>
                  <Typography sx={textStyle}>Card Number</Typography>
                  <TextField
                    id="rfidName"
                    name="rfidName"
                    value={rfidName}
                    onChange={(e) => setRfidName(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        addRfidDetails(e);
                      }
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            data-testid="add-rfid-button"
                            onClick={addRfidDetails}
                            edge="end"
                          >
                            {PlusIcon ? <PlusIcon /> : null}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                  {error && rfidName.length === 0 ? (
                    <Typography sx={textError}>This field is Required * </Typography>
                  ) : (
                    ''
                  )}
                </FormControl>
              </Box>
            </Box>
            {rfidCardList?.length !== 0 && (
              <Grid
                container
                spacing={1}
                sx={{
                  marginTop: { xs: '10px', lg: '30px' },
                  borderTop: '1px solid #B0B9C7',
                  paddingTop: { xs: '10px', lg: '20px' },
                  paddingBottom: { xs: '10px', lg: '20px' }
                }}
              >
                {rfidCardList?.map((item) => (
                  <React.Fragment key={item.id}>
                    <Grid item xs={12} md={7}>
                      <Typography sx={textStyle}>Card UID : {item.rfidCardNo}</Typography>
                    </Grid>
                    <Grid item xs={8} md={4}>
                      <Box sx={{ display: 'flex', gap: '30px', justifyContent: 'center' }}>
                        <Typography
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px',
                            fontSize: '12px',
                            fontWeight: '600'
                          }}
                        >
                          <FiberManualRecordIcon
                            sx={{
                              width: '10px',
                              height: '10px',
                              color: item.status ? '#0DA626' : '#B0B9C7'
                            }}
                          />
                          {item.status ? 'Active' : 'Deactive'}
                        </Typography>
                        <SwitchButton
                          data-testid="rfid-checkbox"
                          handleChange={() => handleRfidStatus(item.id)}
                          value={item.status}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={3} md={1} lg={1} sx={{ display: 'flex', justifyContent: 'end' }}>
                      <DeleteIcon
                        data-testid="delete-rfid-button"
                        fontSize="small"
                        cursor="pointer"
                        onClick={() => deleteRfidDetail(item.id)}
                      />
                    </Grid>
                  </React.Fragment>
                ))}
              </Grid>
            )}
          </Box>
          {/* <Box sx={{ display: 'flex', gap: '10px', marginTop: '20px', alignItems: 'center' }}>
            <Typography sx={textStyle}>Link new RFID card</Typography>
            <PlusIcon style={{ cursor: 'pointer' }} />
          </Box> */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'end',
              gap: '20px',
              alignItems: 'end',
              mt: 'auto'
            }}
          >
            <Button
              variant="outlined"
              color="primary"
              size="small"
              sx={{ boxShadow: 'none', height: '35px', border: '1px solid #EAE9EE' }}
              onClick={handleClose}
            >
              Cancel
            </Button>
            <LoadingButton
              variant="contained"
              color="primary"
              size="small"
              sx={{ boxShadow: 'none', height: '35px' }}
              onClick={addRfidCardData}
              loading={loader}
            >
              Save
            </LoadingButton>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}
